/**
 * TenantsListPage.tsx — Platform admin tenant management
 *
 * Lists all billing tenants with status, plan, Connect status, and fee config.
 * Provides a "Create Tenant" dialog and inline fee configuration modal.
 *
 * @since 2026-06-19 (SaaS conversion — Phase 7)
 * @updated 2026-06-20 (Connect Express + fee config)
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import PlatformLayout from '../components/PlatformLayout';

interface BillingTenant {
  id: string;
  auth_org_id: string;
  name: string;
  slug: string;
  status: string;
  commercial_plan: string;
  platform_fee_pct: string;
  fee_threshold_cents: string;
  hosting_fee_pct: string;
  is_self_hosted: boolean;
  connect_status: string;
  stripe_account_id: string | null;
  created_at: string;
}

function ConnectBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    complete: 'bg-green-100 text-green-700',
    in_progress: 'bg-yellow-100 text-yellow-700',
    pending_verification: 'bg-blue-100 text-blue-700',
    pending: 'bg-slate-100 text-slate-600',
    not_connected: 'bg-slate-100 text-slate-400',
  };
  const labels: Record<string, string> = {
    complete: 'Connected',
    in_progress: 'In Progress',
    pending_verification: 'Verifying',
    pending: 'Pending',
    not_connected: 'Not Connected',
  };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] ?? styles.not_connected}`}>
      {labels[status] ?? status}
    </span>
  );
}

export default function TenantsListPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ auth_org_id: '', name: '', slug: '' });
  const [editTenant, setEditTenant] = useState<BillingTenant | null>(null);
  const [feeForm, setFeeForm] = useState({ platform_fee_pct: '', fee_threshold_cents: '' });

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'tenants'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/platform/tenants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json() as Promise<{ tenants: BillingTenant[] }>;
    },
    enabled: !!token,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiBaseUrl}/platform/tenants`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      setShowCreate(false);
      setForm({ auth_org_id: '', name: '', slug: '' });
    },
  });

  const feeMutation = useMutation({
    mutationFn: async () => {
      if (!editTenant) throw new Error('No tenant selected');
      // Convert display % (e.g. "10.00") to decimal (e.g. "0.10")
      const decimalPct = (parseFloat(feeForm.platform_fee_pct) / 100).toString();
      const res = await fetch(`${apiBaseUrl}/platform/tenants/${editTenant.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          platform_fee_pct: decimalPct,
          fee_threshold_cents: parseInt(feeForm.fee_threshold_cents, 10) || 0,
        }),
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      setEditTenant(null);
    },
  });

  function openFeeEditor(t: BillingTenant): void {
    setEditTenant(t);
    setFeeForm({
      platform_fee_pct: (parseFloat(t.platform_fee_pct) * 100).toFixed(2),
      fee_threshold_cents: String(parseInt(t.fee_threshold_cents, 10) || 0),
    });
  }

  const tenants = data?.tenants ?? [];

  return (
    <PlatformLayout title="Tenants">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">All Billing Tenants</h2>
          <button
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700"
          >
            + Create Tenant
          </button>
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-500">Loading...</p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Plan</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Connect</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Platform Fee</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Threshold</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tenants.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{t.name}</div>
                      <div className="text-xs text-slate-400">{t.slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        t.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : t.status === 'suspended'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{t.commercial_plan}</td>
                    <td className="px-4 py-3">
                      <ConnectBadge status={t.connect_status} />
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {(parseFloat(t.platform_fee_pct) * 100).toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {parseInt(t.fee_threshold_cents, 10) > 0
                        ? `$${(parseInt(t.fee_threshold_cents, 10) / 100).toFixed(2)}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openFeeEditor(t)}
                        className="text-sm text-cyan-600 hover:text-cyan-800 font-medium"
                      >
                        Configure
                      </button>
                    </td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                      No tenants yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Fee Config Modal */}
        {editTenant && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">
                Configure Fees — {editTenant.name}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Set the platform fee percentage and minimum volume threshold for this tenant.
              </p>

              <div className="mt-1 text-xs text-slate-400">
                Connect: <ConnectBadge status={editTenant.connect_status} />
                {editTenant.stripe_account_id && (
                  <span className="ml-2 font-mono">{editTenant.stripe_account_id}</span>
                )}
              </div>

              <div className="mt-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    Platform Fee (%)
                  </label>
                  <p className="text-xs text-slate-500 mb-1">
                    Percentage of each charge kept as platform revenue. Applied as Stripe application_fee.
                  </p>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={feeForm.platform_fee_pct}
                      onChange={(e) => setFeeForm({ ...feeForm, platform_fee_pct: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-8 text-sm"
                    />
                    <span className="absolute right-3 top-2 text-sm text-slate-400">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700">
                    Fee Threshold (cents)
                  </label>
                  <p className="text-xs text-slate-500 mb-1">
                    Minimum gross volume (in cents) before platform fees apply. 0 means fees apply from the first dollar.
                  </p>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={feeForm.fee_threshold_cents}
                    onChange={(e) => setFeeForm({ ...feeForm, fee_threshold_cents: e.target.value })}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                  {parseInt(feeForm.fee_threshold_cents, 10) > 0 && (
                    <p className="mt-1 text-xs text-slate-400">
                      = ${(parseInt(feeForm.fee_threshold_cents, 10) / 100).toFixed(2)} USD
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setEditTenant(null)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => feeMutation.mutate()}
                  disabled={feeMutation.isPending}
                  className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
                >
                  {feeMutation.isPending ? 'Saving...' : 'Save Fee Config'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Tenant Dialog */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">Create Billing Tenant</h3>
              <div className="mt-4 space-y-3">
                <input
                  type="text"
                  placeholder="Auth Org UUID"
                  value={form.auth_org_id}
                  onChange={(e) => setForm({ ...form, auth_org_id: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Tenant Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Slug"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreate(false)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => createMutation.mutate()}
                  disabled={createMutation.isPending}
                  className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
